import { useEffect } from 'react'
import { differenceInDays } from 'date-fns'

import { useQueryString } from '../../../modules/navigation'
import { useFindAddresses } from '../../../modules/quotes'
import { useConfig } from '../../../modules/config'
import { type QuotesRequestFormValues } from './QuotesRequestPage.type'

type GetValidDateOptions = {
  minDateDays?: number
  maxDateDays?: number
}

/**
 * return date (if valid)
 */
const getValidDate = (dateString: string, {
  minDateDays = 0,
  maxDateDays = Infinity,
}: GetValidDateOptions = {}) => {
  if (!/^[0-9]{4}-[0-1][0-9]-[0-3][0-9]$/.test(dateString)) {
    return
  }
  const [year, month, day] = dateString.split('-')

  if (Number(month) > 12 || Number(day) > 31) {
    return
  }

  const date = new Date(Number(year), Number(month) - 1, Number(day))

  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return false
  }

  const now = new Date()
  const daysInFuture = differenceInDays(date, now)

  if (daysInFuture < minDateDays || daysInFuture > maxDateDays) {
    return false
  }

  return date
}

type UseDefaultValuesOptions = {
  skip?: boolean
}

type DeepPartial<T> = T extends object ? {
  [P in keyof T]?: DeepPartial<T[P]>;
} : T

type Response = {
  ready: boolean
  defaultValues?: DeepPartial<QuotesRequestFormValues>
}

export const useDefaultValues = ({
  skip = false,
}: UseDefaultValuesOptions = {}): Response => {
  const {
    originAddress,
    originApt,
    destinationAddress,
    destinationApt,
    movingDate,
  } = useQueryString() ?? {}
  const minDateDays = useConfig<number>('instantBooking.quote.minDateDays')
  const maxDateDays = useConfig<number>('instantBooking.quote.maxDateDays')
  const { findAddresses, data: addresses, called, loading } = useFindAddresses()

  const needToFindAddresses = !skip && (!!originAddress || !!destinationAddress)
  const addressesReady = !needToFindAddresses || (called && !loading)

  /**
   * find addresses provided as query string
   */
  useEffect(() => {
    if (!needToFindAddresses || addressesReady) {
      return
    }
    findAddresses({
      origin: originAddress,
      destination: destinationAddress,
    }).catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needToFindAddresses, addressesReady])

  if (skip) {
    return {
      ready: true,
      defaultValues: undefined,
    }
  }

  return {
    ready: addressesReady,
    defaultValues: {
      addresses: {
        origin: {
          address: !!addresses?.origin || !!originApt
            ? {
                ...addresses?.origin,
                apartment: originApt,
                placeId: undefined,
              }
            : undefined,
        },
        destination: {
          address: !!addresses?.destination || !!destinationApt
            ? {
                ...addresses?.destination,
                apartment: destinationApt,
                placeId: undefined,
              }
            : undefined,
        },
      },
      dateTime: {
        movingDate: getValidDate(movingDate, {
          minDateDays,
          maxDateDays,
        }),
      },
    },
  }
}
